import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';

import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiAlert from '@mui/material/Alert';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import Sidebar from '../../components/sidebar';
import Alert from '../../components/shared/Alert';
import LoadScreen from '../../components/load-screen';

import { toggleSidebar } from '../../actions/sidebarActions';
import { closeNotification } from '../../actions/notificationActions';
import { fetchActivity } from '../../actions/activityActions';
import {
  fetchUsers,
  fetchDistributors,
  fetchCountryList,
  fetchSettings,
  fetchProducts,
  fetchTeams,
  fetchCompanies,
  fetchDocuments,
} from '../../actions/adminActions';
import { savePushNotificationsToken, setCurrentUser } from '../../actions/userActions';
import { fetchProcedures } from '../../actions/proceduresActions';

import { withFirebase } from '../../firebase';
import { routes, userRoles } from '../../constants';

import DashboardPage from '../dashboard';
import CasesPage from '../cases';
import ActivityPage from '../activity';
import CalendarPage from '../calendar';
import UsersPage from '../users';
import SurgeonsPage from '../surgeons';
import DocumentsPage from '../documents';
import {
  CompaniesPage, DistributorsPage, HospitalsPage, ProceduresPage,
  ManufacturersPage, ProductsPage, TeamsPage, RadiologyPage, LogisticsPage
} from '../admin';

const drawerWidth = 280;
const drawerWidthCollapsed = 106;

const ProtectedRoute = ({ allowedRoles, userRole, component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      allowedRoles.includes(userRole) ? (
        <Component {...props} />
      ) : (
        <Redirect to={routes.DASHBOARD} />
      )
    }
  />
);

const AppPage = (props) => {
  const {
    // container,
    mobileOpen,
    toggleSidebar,
    notification,
    closeNotification,
    fetchProcedures,
    fetchActivity,
    firebase,
    fetchUsers,
    userRole,
    fetchDistributors,
    fetchProducts,
    fetchCountryList,
    fetchSettings,
    fetchTeams,
    fetchCompanies,
    fetchDocuments,
    saveToken,
    currentUser,
    setUser,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [ghostLoading, setGhostLoading] = useState(false);
  const [ghostError, setGhostError] = useState('');

  useEffect(() => {
    if (!currentUser?.uid) {
      return undefined;
    }

    let isMounted = true;
    setLoading(true);

    Promise.all([
      fetchActivity(),
      fetchUsers(),
      fetchSettings(),
      fetchDistributors(),
      fetchCountryList(),
      fetchProcedures(),
      fetchProducts(),
      fetchTeams(),
      fetchCompanies(),
      fetchDocuments()
    ]).finally(() => {
      if (isMounted) {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    currentUser?.uid,
    fetchActivity,
    fetchUsers,
    fetchSettings,
    fetchDistributors,
    fetchCountryList,
    fetchProcedures,
    fetchProducts,
    fetchTeams,
    fetchCompanies,
    fetchDocuments,
  ]);

  useEffect(() => {
    if ('Notification' in window) {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          firebase.getNotificationToken().then(saveToken);
        }
      });
    }

    firebase
      .onMessageListener()
      .then((payload) => {
        setNotificationMessage(payload?.data?.body || '');
      })
      .catch(console.error);
  }, [firebase, saveToken]);

  const handleExitGhostMode = async () => {
    try {
      setGhostLoading(true);
      setGhostError('');
      const user = await firebase.stopGhostMode();
      setUser(user);
    } catch (error) {
      console.error(error);
      setGhostError(error?.message || 'Unable to exit ghost mode.');
    } finally {
      setGhostLoading(false);
    }
  };

  return (
    <Fragment>
      {loading && <LoadScreen />}

      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={toggleSidebar}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: collapsed ? drawerWidthCollapsed : drawerWidth,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'var(--color-surface)',
              borderRadius: isMobile ? 0 : '12px',
              margin: isMobile ? 0 : 3,
              maxHeight: isMobile ? '100%' : 'calc(100% - 48px)'
            },
          }}
        >
          <Sidebar
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            isMobile={isMobile}
          />
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: '100vh',
            ml: isMobile
              ? 0
              : collapsed
                ? `${drawerWidthCollapsed}px`
                : `${drawerWidth}px`,
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            pl: isMobile ? 0 : 3,
            backgroundColor: 'var(--color-bg)',
          }}
        >
          {currentUser?.ghostMode && (
            <Box sx={{ px: isMobile ? 2 : 3, pt: isMobile ? 2 : 3 }}>
              <MuiAlert
                severity="warning"
                action={(
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleExitGhostMode}
                    disabled={ghostLoading}
                  >
                    {ghostLoading ? 'Exiting...' : 'Exit Ghost Mode'}
                  </Button>
                )}
                sx={{
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Box sx={{ fontWeight: 600, mb: 0.5 }}>Ghost Mode Active</Box>
                <Box sx={{ fontSize: 14 }}>
                  You are viewing the app as {currentUser.firstName} {currentUser.lastName}
                  {currentUser.email ? ` (${currentUser.email})` : ''}. Exit ghost mode to return to
                  {currentUser.ghostAdminName ? ` ${currentUser.ghostAdminName}` : ' your global admin account'}.
                </Box>
              </MuiAlert>
            </Box>
          )}
          <Switch>
            {/* Public */}
            <Route exact path={routes.DASHBOARD} component={DashboardPage} />
            <Route exact path={routes.CASES} component={CasesPage} />
            <Route exact path={routes.ACTIVITY} component={ActivityPage} />
            <Route exact path={routes.CALENDAR} component={CalendarPage} />

            {/* Global admin */}
            <ProtectedRoute
              exact
              path={routes.PRODUCTS}
              component={ProductsPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.PROCEDURES}
              component={ProceduresPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.MANUFACTURERS}
              component={ManufacturersPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.LOGISTICS}
              component={LogisticsPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.COMPANIES}
              component={CompaniesPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.TEAMS}
              component={TeamsPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.USERS}
              component={UsersPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name, userRoles.COUNTRY_ADMIN.name]}
            />

            <ProtectedRoute
              exact
              path={routes.DOCUMENTS}
              component={DocumentsPage}
              userRole={userRole}
              allowedRoles={[userRoles.GLOBAL_ADMIN.name]}
            />

            {/* Country */}
            <ProtectedRoute
              exact
              path={routes.HOSPITALS}
              component={HospitalsPage}
              userRole={userRole}
              allowedRoles={[
                userRoles.GLOBAL_ADMIN.name,
                userRoles.COUNTRY_ADMIN.name,
              ]}
            />

            <ProtectedRoute
              exact
              path={routes.DISTRIBUTORS}
              component={DistributorsPage}
              userRole={userRole}
              allowedRoles={[
                userRoles.GLOBAL_ADMIN.name,
                userRoles.COUNTRY_ADMIN.name,
              ]}
            />

            <ProtectedRoute
              exact
              path={routes.RADIOLOGY}
              component={RadiologyPage}
              userRole={userRole}
              allowedRoles={[
                userRoles.GLOBAL_ADMIN.name,
                userRoles.COUNTRY_ADMIN.name,
              ]}
            />

            {/* Distributor */}

            <ProtectedRoute
              exact
              path={routes.SURGEONS}
              component={SurgeonsPage}
              userRole={userRole}
              allowedRoles={[
                userRoles.GLOBAL_ADMIN.name,
                userRoles.COUNTRY_ADMIN.name,
                userRoles.DISTRIBUTOR_ADMIN.name,
              ]}
            />

            <Redirect to={routes.DASHBOARD} />
          </Switch>
        </Box>
      </Box>


      {notification?.open && (
        <Alert {...notification} onClose={closeNotification} />
      )}

      <Alert
        variant="warning"
        message={notificationMessage}
        vertical="bottom"
        horizontal="center"
        open={Boolean(notificationMessage)}
        onClose={() => setNotificationMessage('')}
      />

      <Alert
        variant="error"
        message={ghostError}
        vertical="bottom"
        horizontal="center"
        open={Boolean(ghostError)}
        onClose={() => setGhostError('')}
      />
    </Fragment>
  );
};

AppPage.propTypes = {
  mobileOpen: PropTypes.bool,
  userRole: PropTypes.string,
  notification: PropTypes.object,
};

const mapStateToProps = (state) => ({
  users: state.users.list,
  mobileOpen: state.sidebar.mobileOpen,
  userRole: state.user.currentUser.role,
  currentUser: state.user.currentUser,
  notification: state.notification,
});

const mapDispatchToProps = (dispatch) => ({
  toggleSidebar: () => dispatch(toggleSidebar()),
  closeNotification: () => dispatch(closeNotification()),
  fetchActivity: (withoutLoading) => dispatch(fetchActivity(withoutLoading)),
  fetchUsers: () => dispatch(fetchUsers()),
  fetchDistributors: () => dispatch(fetchDistributors()),
  fetchProcedures: () => dispatch(fetchProcedures()),
  fetchProducts: () => dispatch(fetchProducts()),
  fetchTeams: () => dispatch(fetchTeams()),
  fetchCompanies: () => dispatch(fetchCompanies()),
  fetchDocuments: () => dispatch(fetchDocuments()),
  fetchCountryList: () => dispatch(fetchCountryList()),
  fetchSettings: () => dispatch(fetchSettings()),
  saveToken: (token) => dispatch(savePushNotificationsToken(token)),
  setUser: (user) => dispatch(setCurrentUser(user)),
  // subscribeToCases: () => dispatch(subscribeToCases()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withFirebase(AppPage));
